import React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Chip } from '../../components/ui/Chip'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { SlidePanel } from '../../components/ui/SlidePanel'
import { apiFetch } from '../../lib/api-client'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Renter {
  id: string
  tenant_id: string
  company_id: string | null
  name: string
  email: string
  phone: string | null
  language: string
  status: string
  has_cpr: boolean
  renter_type: string
  secondary_email: string | null
  invoice_email: string | null
  prefers_letter: boolean
  cvr_nr: string | null
  ean_number: string | null
  bank_reg_nr: string | null
  bank_account_nr: string | null
  notes: string | null
}

interface RenterAddress {
  id: string
  renter_id: string
  address: string
  start_date: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusVariant(s: string): 'green' | 'amber' | 'gray' {
  return s === 'active' ? 'green' : s === 'applicant' ? 'amber' : 'gray'
}

function statusLabel(s: string): string {
  const map: Record<string, string> = { applicant: 'Ansøger', active: 'Aktiv', former: 'Tidligere' }
  return map[s] ?? s
}

function fmtDate(d: string): string {
  return new Date(d).toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' })
}

type Tab = 'generelt' | 'kontakt' | 'adresse' | 'fakturering' | 'bankoplysninger' | 'lejeforhold' | 'notater'

const TABS: { key: Tab; label: string }[] = [
  { key: 'generelt', label: 'Generelt' },
  { key: 'kontakt', label: 'Kontakt' },
  { key: 'adresse', label: 'Adresse' },
  { key: 'fakturering', label: 'Fakturering' },
  { key: 'bankoplysninger', label: 'Bankoplysninger' },
  { key: 'lejeforhold', label: 'Lejeforhold' },
  { key: 'notater', label: 'Notater' },
]

// ─── Edit Panels ──────────────────────────────────────────────────────────────

function EditGenereltPanel({ renter, onClose, onSaved }: {
  renter: Renter
  onClose: () => void
  onSaved: (r: Renter) => void
}) {
  const [form, setForm] = React.useState({
    name: renter.name,
    email: renter.email,
    phone: renter.phone ?? '',
    language: renter.language,
    status: renter.status,
    renter_type: renter.renter_type,
  })
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) { setError('Navn er påkrævet.'); return }
    setSaving(true); setError(null)
    try {
      const updated = await apiFetch<Renter>(`/api/v1/renters/${renter.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          language: form.language,
          status: form.status,
          renter_type: form.renter_type,
        }),
      })
      onSaved(updated); onClose()
    } catch { setError('Kunne ikke gemme. Prøv igen.') } finally { setSaving(false) }
  }

  const inputWrap: React.CSSProperties = { marginBottom: 14 }
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }

  return (
    <form onSubmit={handleSave}>
      <div style={inputWrap}><Input label="Fulde navn" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus /></div>
      <div style={inputWrap}><Input label="E-mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
      <div style={inputWrap}><Input label="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+45 12 34 56 78" /></div>
      <div style={inputWrap}>
        <label style={labelStyle}>Type</label>
        <select value={form.renter_type} onChange={(e) => setForm({ ...form, renter_type: e.target.value })}
          style={{ height: 40, padding: '0 12px', borderRadius: 7, border: '1px solid #e8eaed', fontSize: 14, width: '100%', fontFamily: 'inherit' }}>
          <option value="privatperson">Privatperson</option>
          <option value="virksomhed">Virksomhed</option>
        </select>
      </div>
      <div style={inputWrap}>
        <label style={labelStyle}>Status</label>
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
          style={{ height: 40, padding: '0 12px', borderRadius: 7, border: '1px solid #e8eaed', fontSize: 14, width: '100%', fontFamily: 'inherit' }}>
          <option value="applicant">Ansøger</option>
          <option value="active">Aktiv</option>
          <option value="former">Tidligere</option>
        </select>
      </div>
      <div style={inputWrap}>
        <label style={labelStyle}>Foretrukket sprog</label>
        <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}
          style={{ height: 40, padding: '0 12px', borderRadius: 7, border: '1px solid #e8eaed', fontSize: 14, width: '100%', fontFamily: 'inherit' }}>
          <option value="da">Dansk</option>
          <option value="en">Engelsk</option>
        </select>
      </div>
      {error && <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 12 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="submit" isLoading={saving}>Gem</Button>
        <Button type="button" variant="secondary" onClick={onClose}>Annuller</Button>
      </div>
    </form>
  )
}

function EditKontaktPanel({ renter, onClose, onSaved }: {
  renter: Renter
  onClose: () => void
  onSaved: (r: Renter) => void
}) {
  const [form, setForm] = React.useState({
    secondary_email: renter.secondary_email ?? '',
    invoice_email: renter.invoice_email ?? '',
    prefers_letter: renter.prefers_letter,
  })
  const [saving, setSaving] = React.useState(false)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const updated = await apiFetch<Renter>(`/api/v1/renters/${renter.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          secondary_email: form.secondary_email.trim() || null,
          invoice_email: form.invoice_email.trim() || null,
          prefers_letter: form.prefers_letter,
        }),
      })
      onSaved(updated); onClose()
    } catch { /* ignore */ } finally { setSaving(false) }
  }

  const inputWrap: React.CSSProperties = { marginBottom: 14 }

  return (
    <form onSubmit={handleSave}>
      <div style={inputWrap}><Input label="Alternativ e-mail" type="email" value={form.secondary_email} onChange={(e) => setForm({ ...form, secondary_email: e.target.value })} /></div>
      <div style={inputWrap}><Input label="Fakturamail" type="email" value={form.invoice_email} onChange={(e) => setForm({ ...form, invoice_email: e.target.value })} /></div>
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.prefers_letter} onChange={(e) => setForm({ ...form, prefers_letter: e.target.checked })} />
          Foretrækker fysisk post
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="submit" isLoading={saving}>Gem</Button>
        <Button type="button" variant="secondary" onClick={onClose}>Annuller</Button>
      </div>
    </form>
  )
}

function EditBankPanel({ renter, onClose, onSaved }: {
  renter: Renter
  onClose: () => void
  onSaved: (r: Renter) => void
}) {
  const [form, setForm] = React.useState({
    bank_reg_nr: renter.bank_reg_nr ?? '',
    bank_account_nr: renter.bank_account_nr ?? '',
  })
  const [saving, setSaving] = React.useState(false)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const updated = await apiFetch<Renter>(`/api/v1/renters/${renter.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          bank_reg_nr: form.bank_reg_nr.trim() || null,
          bank_account_nr: form.bank_account_nr.trim() || null,
        }),
      })
      onSaved(updated); onClose()
    } catch { /* ignore */ } finally { setSaving(false) }
  }

  return (
    <form onSubmit={handleSave}>
      <div style={{ marginBottom: 14 }}>
        <Input label="Registreringsnummer" value={form.bank_reg_nr} onChange={(e) => setForm({ ...form, bank_reg_nr: e.target.value })} placeholder="1234" autoFocus />
      </div>
      <div style={{ marginBottom: 14 }}>
        <Input label="Kontonummer" value={form.bank_account_nr} onChange={(e) => setForm({ ...form, bank_account_nr: e.target.value })} placeholder="12345678" />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="submit" isLoading={saving}>Gem</Button>
        <Button type="button" variant="secondary" onClick={onClose}>Annuller</Button>
      </div>
    </form>
  )
}

function EditFaktureringPanel({ renter, onClose, onSaved }: {
  renter: Renter
  onClose: () => void
  onSaved: (r: Renter) => void
}) {
  const [form, setForm] = React.useState({
    cvr_nr: renter.cvr_nr ?? '',
    ean_number: renter.ean_number ?? '',
  })
  const [saving, setSaving] = React.useState(false)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const updated = await apiFetch<Renter>(`/api/v1/renters/${renter.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          cvr_nr: form.cvr_nr.trim() || null,
          ean_number: form.ean_number.trim() || null,
        }),
      })
      onSaved(updated); onClose()
    } catch { /* ignore */ } finally { setSaving(false) }
  }

  return (
    <form onSubmit={handleSave}>
      <div style={{ marginBottom: 14 }}>
        <Input label="CVR-nr." value={form.cvr_nr} onChange={(e) => setForm({ ...form, cvr_nr: e.target.value })} placeholder="12345678" autoFocus />
      </div>
      <div style={{ marginBottom: 14 }}>
        <Input label="EAN-nummer" value={form.ean_number} onChange={(e) => setForm({ ...form, ean_number: e.target.value })} placeholder="5790000000000" />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="submit" isLoading={saving}>Gem</Button>
        <Button type="button" variant="secondary" onClick={onClose}>Annuller</Button>
      </div>
    </form>
  )
}

// ─── Lejeforhold Tab ─────────────────────────────────────────────────────────

interface RenterContract {
  id: string
  lease_id: string
  type: string
  monthly_rent_oere: number
  start_date: string
  end_date: string | null
  status: string
  serial_number: number | null
}

function RenterLejeforholdTab({ renterId }: { renterId: string }) {
  const navigate = useNavigate()
  const [contracts, setContracts] = React.useState<RenterContract[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    apiFetch<RenterContract[]>(`/api/v1/contracts?renter_id=${renterId}`)
      .then((data) => setContracts(Array.isArray(data) ? data : []))
      .catch(() => setContracts([]))
      .finally(() => setLoading(false))
  }, [renterId])

  const statusLabel = (s: string): string => {
    const map: Record<string, string> = { draft: 'Kladde', sent: 'Sendt', signed: 'Underskrevet', active: 'Aktiv', terminated: 'Opsagt' }
    return map[s] ?? s
  }

  const statusVariantLocal = (s: string): 'green' | 'blue' | 'purple' | 'gray' | 'red' => {
    switch (s) {
      case 'active': return 'green'
      case 'signed': return 'blue'
      case 'sent': return 'purple'
      case 'terminated': return 'red'
      default: return 'gray'
    }
  }

  const fmtDate = (d: string | null): string => {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const fmtRent = (oere: number): string =>
    new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(oere / 100)

  return (
    <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8eaed' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Lejeforhold {!loading && `(${contracts.length})`}
        </span>
      </div>

      {loading && (
        <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>Henter…</div>
      )}

      {!loading && contracts.length === 0 && (
        <div style={{ padding: 48, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>
          Ingen lejeforhold registreret for denne lejer.
        </div>
      )}

      {!loading && contracts.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['Type', 'Indflytning', 'Ophør', 'Månedsleje', 'Status', ''].map((h) => (
                <th key={h} style={{
                  padding: '9px 20px', textAlign: 'left', fontSize: 11,
                  fontWeight: 600, color: '#6b7280', textTransform: 'uppercase',
                  letterSpacing: '0.05em', borderBottom: '1px solid #e8eaed',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {contracts.map((c, i) => (
              <tr
                key={c.id}
                onClick={() => navigate(`/lejeforhold/${c.id}`)}
                style={{ borderBottom: i < contracts.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer' }}
              >
                <td style={{ padding: '11px 20px', color: '#6b7280', textTransform: 'uppercase', fontSize: 11, fontWeight: 600 }}>{c.type}</td>
                <td style={{ padding: '11px 20px', color: '#111827' }}>{fmtDate(c.start_date)}</td>
                <td style={{ padding: '11px 20px', color: '#6b7280' }}>{c.end_date ? fmtDate(c.end_date) : <span style={{ color: '#9ca3af' }}>Løbende</span>}</td>
                <td style={{ padding: '11px 20px', color: '#111827', fontWeight: 500 }}>{fmtRent(c.monthly_rent_oere)}</td>
                <td style={{ padding: '11px 20px' }}>
                  <Chip variant={statusVariantLocal(c.status)}>{statusLabel(c.status)}</Chip>
                </td>
                <td style={{ padding: '11px 20px', textAlign: 'right', color: '#9ca3af' }}>→</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ─── Stamdata Section (shared across tabs) ────────────────────────────────────

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr>
      <td style={{ padding: '10px 20px', fontSize: 13, color: '#6b7280', width: 200, whiteSpace: 'nowrap' }}>
        {label}
      </td>
      <td style={{ padding: '10px 20px', fontSize: 13, color: '#111827', fontWeight: 500 }}>
        {value ?? <span style={{ color: '#cbd5e1' }}>—</span>}
      </td>
    </tr>
  )
}

function SectionCard({
  title,
  onEdit,
  children,
}: {
  title: string
  onEdit?: () => void
  children: React.ReactNode
}) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, marginBottom: 20 }}>
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid #e8eaed',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {title}
        </span>
        {onEdit && <Button size="sm" onClick={onEdit}>Rediger</Button>}
      </div>
      {children}
    </div>
  )
}

// ─── Tab Components ───────────────────────────────────────────────────────────

function GenereltTab({ renter, onUpdated }: { renter: Renter; onUpdated: (r: Renter) => void }) {
  const [editOpen, setEditOpen] = React.useState(false)

  return (
    <>
      <SectionCard title="Stamdata" onEdit={() => setEditOpen(true)}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <Row label="Fulde navn" value={renter.name} />
            <Row label="E-mail" value={renter.email} />
            <Row label="Telefon" value={renter.phone} />
            <Row label="Type" value={renter.renter_type === 'virksomhed' ? 'Virksomhed' : 'Privatperson'} />
            <Row label="Status" value={<Chip variant={statusVariant(renter.status)}>{statusLabel(renter.status)}</Chip>} />
            <Row label="Foretrukket sprog" value={renter.language === 'da' ? 'Dansk' : 'Engelsk'} />
            <Row label="CPR" value={
              renter.has_cpr
                ? <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✓ Registreret</span>
                : <span style={{ fontSize: 12, color: '#94a3b8' }}>Ikke registreret</span>
            } />
          </tbody>
        </table>
      </SectionCard>

      <SlidePanel isOpen={editOpen} onClose={() => setEditOpen(false)} title="Rediger lejer">
        <EditGenereltPanel renter={renter} onClose={() => setEditOpen(false)} onSaved={onUpdated} />
      </SlidePanel>
    </>
  )
}

function KontaktTab({ renter, onUpdated }: { renter: Renter; onUpdated: (r: Renter) => void }) {
  const [editOpen, setEditOpen] = React.useState(false)

  return (
    <>
      <SectionCard title="Kontaktoplysninger" onEdit={() => setEditOpen(true)}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <Row label="E-mail" value={renter.email} />
            <Row label="Alternativ e-mail" value={renter.secondary_email} />
            <Row label="Fakturamail" value={renter.invoice_email} />
            <Row label="Telefon" value={renter.phone} />
            <Row label="Foretrækker post" value={renter.prefers_letter ? 'Ja' : 'Nej'} />
          </tbody>
        </table>
      </SectionCard>

      <SlidePanel isOpen={editOpen} onClose={() => setEditOpen(false)} title="Rediger kontaktoplysninger">
        <EditKontaktPanel renter={renter} onClose={() => setEditOpen(false)} onSaved={onUpdated} />
      </SlidePanel>
    </>
  )
}

function AdresseTab({ renterId }: { renterId: string }) {
  const [addresses, setAddresses] = React.useState<RenterAddress[]>([])
  const [loading, setLoading] = React.useState(true)
  const [addOpen, setAddOpen] = React.useState(false)
  const [form, setForm] = React.useState({ address: '', start_date: '' })
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    apiFetch<RenterAddress[]>(`/api/v1/renters/${renterId}/addresses`)
      .then(setAddresses)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [renterId])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.address.trim() || !form.start_date) return
    setSaving(true)
    try {
      const created = await apiFetch<RenterAddress>(`/api/v1/renters/${renterId}/addresses`, {
        method: 'POST',
        body: JSON.stringify({ address: form.address.trim(), start_date: form.start_date }),
      })
      setAddresses((prev) => [created, ...prev])
      setForm({ address: '', start_date: '' })
      setAddOpen(false)
    } catch { /* ignore */ } finally { setSaving(false) }
  }

  const handleDelete = async (addressId: string) => {
    try {
      await apiFetch(`/api/v1/renters/${renterId}/addresses/${addressId}`, { method: 'DELETE' })
      setAddresses((prev) => prev.filter((a) => a.id !== addressId))
    } catch { /* ignore */ }
  }

  return (
    <>
      <SectionCard title="Adressehistorik" onEdit={() => setAddOpen(true)}>
        {loading && <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>Henter…</div>}
        {!loading && addresses.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>Ingen adresser registreret.</div>
        )}
        {!loading && addresses.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Adresse', 'Fra dato', ''].map((h) => (
                  <th key={h} style={{ padding: '8px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e8eaed' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {addresses.map((a, i) => (
                <tr key={a.id} style={{ borderBottom: i < addresses.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <td style={{ padding: '10px 20px', color: '#0f172a', fontWeight: 500 }}>{a.address}</td>
                  <td style={{ padding: '10px 20px', color: '#64748b' }}>{fmtDate(a.start_date)}</td>
                  <td style={{ padding: '10px 20px', textAlign: 'right' }}>
                    <button onClick={() => handleDelete(a.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, borderRadius: 4, display: 'inline-flex' }}
                      title="Slet adresse">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      <SlidePanel isOpen={addOpen} onClose={() => setAddOpen(false)} title="Tilføj adresse">
        <form onSubmit={handleAdd}>
          <div style={{ marginBottom: 14 }}>
            <Input label="Adresse" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Nørrebrogade 1, 2200 København N" autoFocus />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>Fra dato</label>
            <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              style={{ height: 40, padding: '0 12px', borderRadius: 7, border: '1px solid #e8eaed', fontSize: 14, width: '100%', fontFamily: 'inherit', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="submit" isLoading={saving}>Tilføj</Button>
            <Button type="button" variant="secondary" onClick={() => setAddOpen(false)}>Annuller</Button>
          </div>
        </form>
      </SlidePanel>
    </>
  )
}

function FaktureringTab({ renter, onUpdated }: { renter: Renter; onUpdated: (r: Renter) => void }) {
  const [editOpen, setEditOpen] = React.useState(false)

  return (
    <>
      <SectionCard title="Faktureringsoplysninger" onEdit={() => setEditOpen(true)}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <Row label="CVR-nr." value={renter.cvr_nr} />
            <Row label="EAN-nummer" value={renter.ean_number} />
            <Row label="Fakturamail" value={renter.invoice_email} />
          </tbody>
        </table>
      </SectionCard>

      <SlidePanel isOpen={editOpen} onClose={() => setEditOpen(false)} title="Rediger faktureringsoplysninger">
        <EditFaktureringPanel renter={renter} onClose={() => setEditOpen(false)} onSaved={onUpdated} />
      </SlidePanel>
    </>
  )
}

function BankoplysningerTab({ renter, onUpdated }: { renter: Renter; onUpdated: (r: Renter) => void }) {
  const [editOpen, setEditOpen] = React.useState(false)

  return (
    <>
      <SectionCard title="Bankoplysninger" onEdit={() => setEditOpen(true)}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <Row label="Registreringsnummer" value={renter.bank_reg_nr} />
            <Row label="Kontonummer" value={renter.bank_account_nr} />
          </tbody>
        </table>
        {(renter.bank_reg_nr || renter.bank_account_nr) && (
          <div style={{ padding: '0 20px 16px' }}>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
              Bankoplysninger bruges til tilbagebetaling af depositum.
            </p>
          </div>
        )}
      </SectionCard>

      <SlidePanel isOpen={editOpen} onClose={() => setEditOpen(false)} title="Rediger bankoplysninger">
        <EditBankPanel renter={renter} onClose={() => setEditOpen(false)} onSaved={onUpdated} />
      </SlidePanel>
    </>
  )
}

function NotaterTab({ renter, onUpdated }: { renter: Renter; onUpdated: (r: Renter) => void }) {
  const [notes, setNotes] = React.useState(renter.notes ?? '')
  const [saving, setSaving] = React.useState(false)

  const handleBlur = async () => {
    if (notes === (renter.notes ?? '')) return
    setSaving(true)
    try {
      const updated = await apiFetch<Renter>(`/api/v1/renters/${renter.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ notes: notes || null }),
      })
      onUpdated(updated)
    } catch { /* ignore */ } finally { setSaving(false) }
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10 }}>
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid #e8eaed',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Notater
        </span>
        {saving && <span style={{ fontSize: 11, color: '#94a3b8' }}>Gemmer…</span>}
      </div>
      <div style={{ padding: 20 }}>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={handleBlur}
          placeholder="Notater kan anføres her..."
          style={{
            width: '100%', minHeight: 200, border: '1px solid #e8eaed',
            borderRadius: 6, padding: 12, fontSize: 13, color: '#0f172a',
            fontFamily: 'inherit', resize: 'vertical', outline: 'none',
            boxSizing: 'border-box', lineHeight: 1.6,
          }}
        />
      </div>
    </div>
  )
}

// ─── Page Shell ───────────────────────────────────────────────────────────────

export function RenterDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [renter, setRenter] = React.useState<Renter | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [notFound, setNotFound] = React.useState(false)
  const [activeTab, setActiveTab] = React.useState<Tab>('generelt')

  React.useEffect(() => {
    if (!id) return
    apiFetch<Renter>(`/api/v1/renters/${id}`)
      .then(setRenter)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <AppLayout>
        <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Henter…</div>
      </AppLayout>
    )
  }

  if (notFound || !renter) {
    return (
      <AppLayout>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Lejeren blev ikke fundet.</p>
          <Link to="/lejere"><Button variant="secondary">← Tilbage</Button></Link>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <div style={{ padding: '0 0 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: '#4f46e5',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 14, fontWeight: 700, flexShrink: 0,
          }}>
            {renter.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>{renter.name}</h1>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{renter.email}</span>
          </div>
          <Chip variant={statusVariant(renter.status)}>{statusLabel(renter.status)}</Chip>
        </div>
        <Link to="/lejere">
          <Button variant="secondary" size="sm">← Alle lejere</Button>
        </Link>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e8eaed', marginBottom: 20, overflowX: 'auto' }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '11px 16px', fontSize: 13,
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? '#111827' : '#6b7280',
              background: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #4f46e5' : '2px solid transparent',
              marginBottom: -1, cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'color 0.12s, border-color 0.12s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'generelt' && <GenereltTab renter={renter} onUpdated={setRenter} />}
      {activeTab === 'kontakt' && <KontaktTab renter={renter} onUpdated={setRenter} />}
      {activeTab === 'adresse' && <AdresseTab renterId={renter.id} />}
      {activeTab === 'fakturering' && <FaktureringTab renter={renter} onUpdated={setRenter} />}
      {activeTab === 'bankoplysninger' && <BankoplysningerTab renter={renter} onUpdated={setRenter} />}
      {activeTab === 'lejeforhold' && <RenterLejeforholdTab renterId={renter.id} />}
      {activeTab === 'notater' && <NotaterTab renter={renter} onUpdated={setRenter} />}
    </AppLayout>
  )
}
